import React, { useState, useMemo } from 'react';
import { clsx } from 'clsx';
import { MagnifyingGlassIcon, XMarkIcon } from '@/components/icons';

import type { FilterSearchProps } from '@/types/filters';

/**
 * Filter Search Component
 * 
 * Provides search functionality within the filter sidebar:
 * - Search within filter names and descriptions
 * - Auto-suggestions
 * - Clear functionality
 * - Loading states
 */
export function FilterSearch({
  value,
  onChange,
  onClear,
  placeholder = "Search filters...",
  suggestions = [],
  onSuggestionSelect,
  loading = false,
  className
}: FilterSearchProps) {

  const [focused, setFocused] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);

  // Filter suggestions based on current value
  const filteredSuggestions = useMemo(() => {
    if (!value || value.length < 2) return [];
    
    return suggestions
      .filter(suggestion => 
        suggestion.toLowerCase().includes(value.toLowerCase()) &&
        suggestion.toLowerCase() !== value.toLowerCase()
      )
      .slice(0, 5); // Limit to 5 suggestions
  }, [suggestions, value]);

  // Handle input change
  const handleChange = (newValue: string) => {
    onChange(newValue);
    setShowSuggestions(newValue.length >= 2 && filteredSuggestions.length > 0);
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion: string) => {
    onChange(suggestion);
    setShowSuggestions(false);
    onSuggestionSelect?.(suggestion);
  };

  // Handle clear
  const handleClear = () => {
    onChange('');
    setShowSuggestions(false);
    onClear();
  };

  // Handle focus
  const handleFocus = () => {
    setFocused(true);
    if (value && filteredSuggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  // Handle blur
  const handleBlur = () => {
    setFocused(false);
    // Delay hiding suggestions to allow clicks
    setTimeout(() => setShowSuggestions(false), 150);
  };

  // Handle key down
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  return (
    <div className={clsx('relative', className)}>
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          {loading ? (
            <div className="animate-spin w-4 h-4 border-2 border-gray-300 border-t-primary-600 rounded-full" />
          ) : (
            <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
          )}
        </div>
        
        <input
          type="text"
          value={value}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={clsx(
            'w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md text-sm',
            'focus:ring-2 focus:ring-primary-500 focus:border-primary-500',
            'placeholder-gray-400',
            focused && 'ring-2 ring-primary-500 border-primary-500',
            'transition-colors duration-200'
          )}
        />
        
        {/* Clear Button */}
        {value && (
          <button
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            title="Clear search"
          >
            <XMarkIcon className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Suggestions Dropdown */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <>
          <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
            <div className="py-1">
              {filteredSuggestions.map((suggestion, index) => (
                <button
                  key={`${suggestion}-${index}`}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={clsx(
                    'w-full px-3 py-2 text-left text-sm text-gray-700',
                    'hover:bg-gray-50 focus:bg-gray-50 focus:outline-none',
                    'transition-colors duration-150'
                  )}
                >
                  <div className="flex items-center">
                    <MagnifyingGlassIcon className="w-4 h-4 mr-3 text-gray-400" />
                    <span>{suggestion}</span>
                  </div>
                </button>
              ))}
            </div>
            
            {/* Footer */}
            <div className="px-3 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-500">
              Press Enter to search or click a suggestion
            </div>
          </div>
          
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-10"
            onClick={() => setShowSuggestions(false)}
            aria-hidden="true"
          />
        </>
      )}

      {/* No Suggestions Message */}
      {focused && value && value.length >= 2 && filteredSuggestions.length === 0 && suggestions.length > 0 && (
        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
          <div className="px-3 py-3 text-sm text-gray-500 text-center">
            No matching suggestions found
          </div>
        </div>
      )}
    </div>
  );
}